import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import AvatarRing, { type AvatarRingState } from "ui/AvatarRing";
import {
  formatActorString,
  getActorUiStrings,
  resolveActorLocale,
} from "i18n/actorUi";
import {
  getActorDisplayName,
  getActorInitials,
  getActorProfileHref,
  getActorStoryHref,
  resolveActorAvatarUrl,
} from "util/actorIdentity";
import {
  resolveActorAvatarSize,
  type ActorAvatarSizeName,
} from "util/actorAvatarSizes";
import type { ActorRef } from "types/actor";
import { useActorStoryStatus } from "components/Stories/useActorStoryStatus";
import { cn } from "util/cn";

/**
 * Click semantics for an actor avatar:
 *
 *   - `"auto"`     (default): if the actor has an active story, link to
 *                  the story viewer; otherwise link to the actor's
 *                  profile. This is the standard Instagram contract and
 *                  what almost every surface should use.
 *   - `"profile"`: always link to the actor's profile, never to a
 *                  story. Used inside chat message bubbles where story
 *                  navigation in-thread would be jarring.
 *   - `"story"`:   force the story link when one is available; falls
 *                  back to the profile when not. Rarely needed.
 *   - `"none"`:    pure visual, no `<Link>` wrapper. Use this when the
 *                  avatar sits inside a larger clickable row whose own
 *                  click target handles navigation (e.g. notifications
 *                  rows that open the actor's profile via a row click).
 */
export type ActorAvatarClickTo = "auto" | "story" | "profile" | "none";

export interface ActorAvatarProps {
  /** Canonical actor reference. Pass `null` for anonymous content; the avatar will render the fallback initials with no link. */
  actor: ActorRef;
  /**
   * Pixel size or named size token. Prefer the named tokens from
   * `util/actorAvatarSizes` so sizing stays consistent with other
   * surfaces.
   */
  size: number | ActorAvatarSizeName;
  /** Click destination policy. See `ActorAvatarClickTo` for details. */
  clickTo?: ActorAvatarClickTo;
  /**
   * When `false`, suppress the story ring entirely even when the actor
   * has an active story. Defaults to `true`. The chat message-bubble
   * avatar sets this to `false` to keep the in-thread experience quiet.
   */
  showRing?: boolean;
  /** Override the auto-generated aria-label. */
  ariaLabelOverride?: string;
  /** Optional extra classes applied to the outer wrapper. */
  className?: string;
}

/**
 * Standardized, Instagram-style actor avatar used everywhere a User or
 * Partea is shown. Renders the avatar image (resolved from
 * `IMAGE_CDN + avatar_key`), a story ring when the actor has a story,
 * and the appropriate click target.
 *
 * Surfaces that need to combine the avatar with other clickable
 * elements (e.g. a chat row that opens the conversation when the
 * non-avatar area is clicked) should set `clickTo="auto"` here and
 * keep their own button outside the avatar — the avatar will be its
 * own link, exactly like Instagram's chat list.
 */
const ActorAvatar: React.FC<ActorAvatarProps> = ({
  actor,
  size,
  clickTo = "auto",
  showRing = true,
  ariaLabelOverride,
  className,
}) => {
  const pixelSize = resolveActorAvatarSize(size);
  const displayName = getActorDisplayName(actor);
  const initials = getActorInitials(actor);
  const imageUrl = resolveActorAvatarUrl(actor);

  const ui = useMemo(() => getActorUiStrings(resolveActorLocale()), []);

  // Story status drives both the ring color and the auto click target.
  // We always call the hook (Rules of Hooks) but it short-circuits to a
  // no-op when the actor is null or a Team.
  const { hasStory, hasUnseen } = useActorStoryStatus(actor);

  const ringState: AvatarRingState = !showRing || !hasStory
    ? "none"
    : hasUnseen
      ? "unseen"
      : "seen";

  const profileHref = getActorProfileHref(actor);
  const storyHref = getActorStoryHref(actor);

  // Resolve the actual destination per `clickTo` policy.
  const destination = resolveDestination({
    clickTo,
    hasStory,
    storyHref,
    profileHref,
  });

  const ariaLabel =
    ariaLabelOverride ??
    (destination?.kind === "story"
      ? formatActorString(ui.viewStoryAria, displayName)
      : destination?.kind === "profile"
        ? formatActorString(ui.openProfileAria, displayName)
        : formatActorString(ui.avatarAlt, displayName));

  const ring = (
    <AvatarRing
      size={pixelSize}
      ringState={ringState}
      imageUrl={imageUrl}
      imageAlt={formatActorString(ui.avatarAlt, displayName)}
      initials={initials}
    />
  );

  if (!destination) {
    // No clickable destination (clickTo="none" or anonymous actor with
    // no profile). Render the visual only.
    return (
      <span
        className={cn("inline-flex items-center justify-center", className)}
        aria-label={clickTo === "none" ? undefined : ariaLabel}
        aria-hidden={clickTo === "none" ? true : undefined}
      >
        {ring}
      </span>
    );
  }

  return (
    <Link
      to={destination.href}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-soc-purple",
        className
      )}
      data-testid="actor-avatar"
      data-click-to={destination.kind}
      data-has-story={hasStory ? "true" : "false"}
      data-has-unseen={hasUnseen ? "true" : "false"}
    >
      {ring}
    </Link>
  );
};

interface ResolveDestinationParams {
  clickTo: ActorAvatarClickTo;
  hasStory: boolean;
  storyHref: string | null;
  profileHref: string | null;
}

interface ResolvedDestination {
  kind: "story" | "profile";
  href: string;
}

/**
 * Translate the `clickTo` policy + story availability + URL availability
 * into a single concrete destination (or `null` when no link should be
 * rendered). Centralized so the policy table is testable.
 */
function resolveDestination(
  params: ResolveDestinationParams
): ResolvedDestination | null {
  const { clickTo, hasStory, storyHref, profileHref } = params;

  if (clickTo === "none") {
    return null;
  }

  if (clickTo === "profile") {
    return profileHref ? { kind: "profile", href: profileHref } : null;
  }

  if (clickTo === "story") {
    if (hasStory && storyHref) {
      return { kind: "story", href: storyHref };
    }
    return profileHref ? { kind: "profile", href: profileHref } : null;
  }

  // "auto": story when available, profile otherwise.
  if (hasStory && storyHref) {
    return { kind: "story", href: storyHref };
  }
  return profileHref ? { kind: "profile", href: profileHref } : null;
}

export default ActorAvatar;
